import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { API_BASE_URL, FALLBACK_LANGUAGE, detectBrowserLanguage } from "../config";
import { AuthPanel } from "../components/AuthPanel";
import { DEFAULT_AUTH_MESSAGE, login, register, getAuthHeaders } from "../api/auth";
import { resolveErrorMessage } from "../lib/api";
import { UICompositionView } from "./UICompositionView";
import { WorkflowStandaloneView } from "./WorkflowStandaloneView";
import type { UIResourceResponse } from "./types";

interface StandaloneViewProps {
  resourceId: string;
  search?: string;
  language?: string;
  apiBaseUrl?: string;
  onNotFound?: () => void;
}

type ResourceState =
  | { status: "loading" }
  | { status: "unauthorized"; message?: string }
  | { status: "error"; message: string }
  | { status: "loaded"; response: UIResourceResponse };

export function StandaloneView(props: StandaloneViewProps) {
  const resourceId = props.resourceId;
  const apiBaseUrl = props.apiBaseUrl ?? API_BASE_URL;
  const initialLanguage = useMemo(() => props.language ?? detectBrowserLanguage(), [props.language]);
  const [language] = useState(initialLanguage ?? FALLBACK_LANGUAGE);
  const queryEntries = useMemo(() => Array.from(new URLSearchParams(props.search ?? "")), [props.search]);
  const [state, setState] = useState<ResourceState>({ status: "loading" });
  const [authFeedback, setAuthFeedback] = useState<{ type: "info" | "error"; message: string } | null>(null);
  const [authSubmitting, setAuthSubmitting] = useState<"login" | "signup" | null>(null);
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [signupForm, setSignupForm] = useState({ email: "", password: "", displayName: "" });

  const fetchResource = useCallback(async () => {
    setState({ status: "loading" });

    try {
      const searchParams = new URLSearchParams(queryEntries);
      if (!searchParams.has("language")) {
        searchParams.set("language", language);
      }

      const response = await fetch(
        `${apiBaseUrl}/api/ui/${encodeURIComponent(resourceId)}?${searchParams.toString()}`,
        {
          credentials: "include",
          headers: {
            ...getAuthHeaders(),
            "Accept-Language": language
          }
        }
      );

      if (response.status === 404) {
        setState({ status: "error", message: "Die angeforderte UI-Ressource wurde nicht gefunden." });
        props.onNotFound?.();
        return;
      }

      if (response.status === 401) {
        let message = DEFAULT_AUTH_MESSAGE;
        const contentType = response.headers.get("content-type") ?? "";

        try {
          if (contentType.includes("application/json")) {
            const payload = (await response.json()) as Record<string, unknown> | null;
            const maybeMessage = payload && typeof payload.message === "string" ? payload.message.trim() : "";
            if (maybeMessage) {
              message = maybeMessage;
            }
          } else {
            const text = await response.text();
            if (typeof text === "string" && text.trim().length > 0) {
              message = text.trim();
            }
          }
        } catch {
          // ignore parsing problems and fall back to default message
        }

        setAuthFeedback({ type: "info", message });
        setState({ status: "unauthorized", message });
        return;
      }

      if (!response.ok) {
        throw new Error(`Anfrage fehlgeschlagen (${response.status})`);
      }

      const payload = (await response.json()) as UIResourceResponse;
      setAuthFeedback(null);
      setState({ status: "loaded", response: payload });
    } catch (error) {
      console.error(error);
      setState({
        status: "error",
        message: error instanceof Error ? error.message : "Unbekannter Fehler beim Laden der UI"
      });
    }
  }, [apiBaseUrl, language, props.onNotFound, queryEntries, resourceId]);

  const handleLoginFormChange = useCallback((next: { email: string; password: string }) => {
    setLoginForm(next);
  }, []);

  const handleSignupFormChange = useCallback(
    (next: { email: string; password: string; displayName: string }) => {
      setSignupForm(next);
    },
    []
  );

  const handleLoginSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      if (authSubmitting) {
        return;
      }

      setAuthSubmitting("login");
      setAuthFeedback(null);

      try {
        await login(
          {
            email: loginForm.email.trim(),
            password: loginForm.password
          },
          apiBaseUrl
        );

        setLoginForm((prev) => ({ ...prev, password: "" }));
        setAuthFeedback(null);
        await fetchResource();
      } catch (error) {
        const backendMessage =
          resolveErrorMessage(error) ?? "Anmeldung fehlgeschlagen. Bitte Zugangsdaten prüfen.";
        setAuthFeedback({ type: "error", message: backendMessage });
      } finally {
        setAuthSubmitting(null);
      }
    },
    [apiBaseUrl, authSubmitting, fetchResource, loginForm.email, loginForm.password]
  );

  const handleSignupSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      if (authSubmitting) {
        return;
      }

      setAuthSubmitting("signup");
      setAuthFeedback(null);

      try {
        await register(
          {
            email: signupForm.email.trim(),
            password: signupForm.password,
            displayName: signupForm.displayName.trim() || undefined
          },
          apiBaseUrl
        );

        setSignupForm((prev) => ({ ...prev, password: "" }));
        setAuthFeedback(null);
        await fetchResource();
      } catch (error) {
        const backendMessage =
          resolveErrorMessage(error) ?? "Registrierung fehlgeschlagen. Bitte Eingaben prüfen.";
        setAuthFeedback({ type: "error", message: backendMessage });
      } finally {
        setAuthSubmitting(null);
      }
    },
    [apiBaseUrl, authSubmitting, fetchResource, signupForm.displayName, signupForm.email, signupForm.password]
  );

  const handleUnauthorized = useCallback((message?: string) => {
    const feedbackMessage = message && message.trim().length > 0 ? message.trim() : DEFAULT_AUTH_MESSAGE;
    setAuthSubmitting(null);
    setAuthFeedback({ type: "info", message: feedbackMessage });
    setState((prev) => {
      if (prev.status === "unauthorized" && prev.message === feedbackMessage) {
        return prev;
      }
      return { status: "unauthorized", message: feedbackMessage } as ResourceState;
    });
  }, []);

  useEffect(() => {
    void fetchResource();
  }, [fetchResource]);

  if (state.status === "loading") {
    return (
      <div className="standalone-loading">
        <div className="standalone-spinner" aria-hidden="true" />
        <p>UI wird geladen …</p>
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <div className="standalone-error">
        <h1>UI konnte nicht geladen werden</h1>
        <p>{state.message}</p>
      </div>
    );
  }

  if (state.status === "unauthorized") {
    const feedback = authFeedback ?? (state.message ? { type: "info", message: state.message } : null);
    return (
      <div className="standalone-auth">
        <AuthPanel
          className="auth-panel-inline"
          feedback={feedback}
          authSubmitting={authSubmitting}
          loginForm={loginForm}
          signupForm={signupForm}
          onLoginChange={handleLoginFormChange}
          onSignupChange={handleSignupFormChange}
          onLoginSubmit={handleLoginSubmit}
          onSignupSubmit={handleSignupSubmit}
        />
      </div>
    );
  }

  if (state.status !== "loaded") {
    return null;
  }

  if (state.response.kind === "uiComposition") {
    return (
      <UICompositionView
        composition={state.response.composition}
        resourceIdentifier={resourceId}
        apiBaseUrl={apiBaseUrl}
        language={language}
        queryEntries={queryEntries}
        onUnauthorized={handleUnauthorized}
      />
    );
  }

  if (state.response.kind === "workflow") {
    return (
      <WorkflowStandaloneView
        workflowId={state.response.id}
        workflow={state.response.workflow}
        apiBaseUrl={apiBaseUrl}
        language={language}
        onUnauthorized={handleUnauthorized}
      />
    );
  }

  return (
    <div className="standalone-error">
      <h1>Unbekannter UI-Typ</h1>
      <p>Die angeforderte Ressource konnte nicht dargestellt werden.</p>
    </div>
  );
}

export function parseStandalonePath(pathname: string): { id: string; language?: string } | null {
  if (!pathname || pathname === "/") {
    return null;
  }

  const normalized = decodeURIComponent(pathname);
  const directMatch = normalized.match(/^\/ui\/(.+)$/);
  if (directMatch) {
    return { id: directMatch[1] };
  }

  const apiMatch = normalized.match(/^\/api\/ui\/(.+)$/);
  if (apiMatch) {
    return { id: apiMatch[1] };
  }

  return null;
}

export function parseLanguageFromSearch(search: string | null | undefined): string | undefined {
  if (!search) {
    return undefined;
  }

  const params = new URLSearchParams(search);
  const language = params.get("lang") ?? params.get("language");
  if (!language) {
    return undefined;
  }

  const trimmed = language.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

export function resolveStandaloneLanguage(search: string | null | undefined): string {
  return parseLanguageFromSearch(search) ?? detectBrowserLanguage() ?? FALLBACK_LANGUAGE;
}
